import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import FileSlot from "../components/FileSlot";
import { ChevronRight, ArrowLeft, Check, AlertCircle, Play, Wand2 } from "lucide-react";

const STEPS = [
  { id: 1, label: "Upload Files" },
  { id: 2, label: "Select Rule Set" },
  { id: 3, label: "Review & Run" },
];

function StepIndicator({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: "2rem" }}>
      {STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.375rem" }}>
            <div className={s.id <= current ? "step-circle-active" : "step-circle-inactive"}>
              {s.id < current ? <Check size={14} /> : s.id}
            </div>
            <span style={{ fontSize: "0.75rem", color: s.id <= current ? "var(--sage)" : "var(--text-muted)", fontWeight: s.id === current ? 600 : 400, whiteSpace: "nowrap" }}>
              {s.label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={i + 1 < current ? "step-line-active" : "step-line"} style={{ flexGrow: 1, minWidth: "3rem" }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function ReconciliationWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [fileA, setFileA] = useState(null);
  const [fileB, setFileB] = useState(null);
  const [fileATsId, setFileATsId] = useState("");
  const [fileBTsId, setFileBTsId] = useState("");
  const [transformSets, setTransformSets] = useState([]);
  const [ruleSets, setRuleSets] = useState([]);
  const [selectedRS, setSelectedRS] = useState(null);
  const [runName, setRunName] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/rule-sets/").then(r => setRuleSets(r.data));
    api.get("/transform-sets/").then(r => setTransformSets(r.data)).catch(() => {});
  }, []);

  const canNext1 = fileA && fileB;
  const canNext2 = selectedRS;

  const handleRun = async () => {
    setError("");
    if (!runName.trim()) { setError("Please enter a run name"); return; }
    setRunning(true);
    try {
      const fileAInputs = [
        { file_id: fileA.file_id, sheet_name: fileA.sheet || null, skip_rows: Number(fileA.skip_rows || 0) },
        ...(fileA.extra_files || []).map(ef => ({ file_id: ef.file_id, sheet_name: ef.sheet || null, skip_rows: Number(fileA.skip_rows || 0), col_mapping: ef.col_mapping || {} })),
      ];
      const fileBInputs = [
        { file_id: fileB.file_id, sheet_name: fileB.sheet || null, skip_rows: Number(fileB.skip_rows || 0) },
        ...(fileB.extra_files || []).map(ef => ({ file_id: ef.file_id, sheet_name: ef.sheet || null, skip_rows: Number(fileB.skip_rows || 0), col_mapping: ef.col_mapping || {} })),
      ];
      const res = await api.post("/reconciliation/run", {
        name: runName,
        file_a: fileAInputs,
        file_b: fileBInputs,
        file_a_merge_mode: fileA.merge_mode || "superset",
        file_b_merge_mode: fileB.merge_mode || "superset",
        rule_set_id: selectedRS.id,
        file_a_transform_set_id: fileATsId || null,
        file_b_transform_set_id: fileBTsId || null,
      });
      navigate(`/results/${res.data.run_id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Reconciliation failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: "740px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex" }}>
            <ArrowLeft size={20} strokeWidth={1.5} />
          </button>
          <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.5rem", color: "var(--text-primary)", margin: 0 }}>
            New Reconciliation
          </h1>
        </div>

        <StepIndicator current={step} />

        {/* Step 1: Upload Files */}
        {step === 1 && (
          <div className="recon-card" style={{ padding: "1.5rem" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.125rem", color: "var(--text-primary)", margin: "0 0 0.5rem" }}>
              Upload Files to Reconcile
            </h3>
            <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "0 0 1.1rem" }}>
              For files with banner rows above the real header, set "Skip top rows". Empty/unnamed columns are dropped automatically. Save a template to reuse the same sheet + skip on future files.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
              <div>
                <FileSlot
                  testIdPrefix="file-a"
                  label="File A (Source)"
                  accent="var(--sage)"
                  file={fileA}
                  onChange={setFileA}
                  onClear={() => setFileA(null)}
                />
                <div style={{ marginTop: "0.75rem" }}>
                  <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.3rem" }}>
                    <Wand2 size={11} /> Transform Set (optional)
                  </label>
                  <select data-testid="file-a-ts-select" className="recon-input" style={{ width: "100%" }}
                    value={fileATsId} onChange={e => setFileATsId(e.target.value)}>
                    <option value="">— none —</option>
                    {transformSets.map(ts => <option key={ts.id} value={ts.id}>{ts.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <FileSlot
                  testIdPrefix="file-b"
                  label="File B (Target)"
                  accent="#1E40AF"
                  file={fileB}
                  onChange={setFileB}
                  onClear={() => setFileB(null)}
                />
                <div style={{ marginTop: "0.75rem" }}>
                  <label style={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.3rem" }}>
                    <Wand2 size={11} /> Transform Set (optional)
                  </label>
                  <select data-testid="file-b-ts-select" className="recon-input" style={{ width: "100%" }}
                    value={fileBTsId} onChange={e => setFileBTsId(e.target.value)}>
                    <option value="">— none —</option>
                    {transformSets.map(ts => <option key={ts.id} value={ts.id}>{ts.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
              <button data-testid="step1-next" className="btn-primary" onClick={() => setStep(2)} disabled={!canNext1}
                style={{ display: "flex", alignItems: "center", gap: "0.5rem", opacity: canNext1 ? 1 : 0.5, cursor: canNext1 ? "pointer" : "not-allowed" }}>
                Next: Select Rule Set <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Select Rule Set */}
        {step === 2 && (
          <div className="recon-card" style={{ padding: "1.5rem" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.125rem", color: "var(--text-primary)", margin: "0 0 1.25rem" }}>
              Select Rule Set
            </h3>
            {ruleSets.length === 0 ? (
              <div style={{ padding: "2rem", textAlign: "center" }}>
                <p style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>No rule sets found. Ask your department admin to create one.</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {ruleSets.map(rs => (
                  <div key={rs.id}
                    data-testid={`rs-option-${rs.id}`}
                    onClick={() => setSelectedRS(rs)}
                    style={{
                      padding: "1rem 1.25rem", borderRadius: "0.5rem",
                      border: `2px solid ${selectedRS?.id === rs.id ? "var(--sage)" : "var(--border-default)"}`,
                      background: selectedRS?.id === rs.id ? "var(--sage-light)" : "white",
                      cursor: "pointer", transition: "all 0.15s",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <p style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--text-primary)", margin: 0 }}>{rs.name}</p>
                        {rs.description && <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0.25rem 0 0" }}>{rs.description}</p>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <span style={{ fontSize: "0.75rem", background: "var(--bg-subtle)", padding: "0.125rem 0.5rem", borderRadius: "9999px", color: "var(--text-muted)" }}>
                          {(rs.rules || []).length} rules
                        </span>
                        {selectedRS?.id === rs.id && <Check size={16} style={{ color: "var(--sage)" }} />}
                      </div>
                    </div>
                    {selectedRS?.id === rs.id && rs.rules?.length > 0 && (
                      <div style={{ marginTop: "0.75rem", display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
                        {rs.rules.map((r, i) => (
                          <span key={i} style={{ fontSize: "0.7rem", background: "rgba(92,122,94,0.1)", color: "var(--sage)", padding: "0.125rem 0.5rem", borderRadius: "9999px", border: "1px solid var(--sage-border)" }}>
                            {i + 1}. {r.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1.5rem" }}>
              <button className="btn-secondary" onClick={() => setStep(1)} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <ArrowLeft size={16} /> Back
              </button>
              <button data-testid="step2-next" className="btn-primary" onClick={() => setStep(3)} disabled={!canNext2}
                style={{ display: "flex", alignItems: "center", gap: "0.5rem", opacity: canNext2 ? 1 : 0.5, cursor: canNext2 ? "pointer" : "not-allowed" }}>
                Next: Review <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review & Run */}
        {step === 3 && (
          <div className="recon-card" style={{ padding: "1.5rem" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.125rem", color: "var(--text-primary)", margin: "0 0 1.25rem" }}>
              Review & Run
            </h3>

            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ fontSize: "0.875rem", fontWeight: 500, color: "var(--text-secondary)", display: "block", marginBottom: "0.375rem" }}>Run Name *</label>
              <input data-testid="run-name-input" className="recon-input" style={{ width: "100%", boxSizing: "border-box" }}
                value={runName} onChange={e => setRunName(e.target.value)}
                placeholder="e.g. March 2024 Bank Reconciliation" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginBottom: "1.25rem" }}>
              {[
                { label: "File A", value: fileA?.filename, sub: `${fileA?.row_count} rows · skip ${fileA?.skip_rows || 0}` },
                { label: "File B", value: fileB?.filename, sub: `${fileB?.row_count} rows · skip ${fileB?.skip_rows || 0}` },
                { label: "Rule Set", value: selectedRS?.name, sub: `${selectedRS?.rules?.length || 0} rules` },
              ].map(item => (
                <div key={item.label} style={{ padding: "0.875rem 1rem", background: "var(--bg-default)", borderRadius: "0.5rem", border: "1px solid var(--border-default)" }}>
                  <p style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", fontWeight: 600, margin: "0 0 0.25rem" }}>{item.label}</p>
                  <p style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--text-primary)", margin: "0 0 0.125rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.value}</p>
                  <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: 0 }}>{item.sub}</p>
                </div>
              ))}
            </div>

            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "0.5rem", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                <AlertCircle size={16} style={{ color: "#DC2626", flexShrink: 0 }} />
                <p style={{ fontSize: "0.875rem", color: "#991B1B", margin: 0 }}>{error}</p>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5rem" }}>
              <button className="btn-secondary" onClick={() => setStep(2)} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <ArrowLeft size={16} /> Back
              </button>
              <button data-testid="run-recon-btn" className="btn-primary" onClick={handleRun} disabled={running || !runName.trim()}
                style={{ display: "flex", alignItems: "center", gap: "0.5rem", opacity: running ? 0.7 : 1 }}>
                {running ? (
                  <>
                    <div style={{ width: "1rem", height: "1rem", border: "2px solid rgba(255,255,255,0.4)", borderTop: "2px solid white", borderRadius: "9999px", animation: "spin 0.8s linear infinite" }} />
                    Running...
                  </>
                ) : (
                  <><Play size={16} /> Run Reconciliation</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );
}
